import {
  ElementRef,
  FocusEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import { translate } from '@vimeo/i18n-runtime';

import { KEY_CODES, REGISTRATION_FORM_MAX_CHARACTERS } from '../../utils/constants';
import { hasAutoFilled, makeId } from '../../utils/helper';
import Tooltip from '../common/Tooltip';
import { StyledError } from './';

interface Props {
  placeholder?: string;
  value?: string;
  required?: boolean;
  autoFocus?: boolean;
  errorMessage?: string;
  prefillValue?: string;
  validator?: (value: string) => undefined | string; // True if valid, Error string if invalid
  onBlur?: (value: string) => void; // True if valid, Error string if invalid
  onEnterKeyPressed?: (value: string) => void; // True if valid, Error string if invalid
  onClearPrefill?: () => void;
}
export const Input = forwardRef<{ validate: () => boolean; getValue: () => string }, Props>(
  (
    {
      value,
      placeholder,
      required,
      autoFocus,
      errorMessage,
      prefillValue,
      validator,
      onBlur,
      onEnterKeyPressed,
      onClearPrefill,
    }: Props,
    ref,
  ) => {
    const [error, setError] = useState<string>();
    const [_value, _setValue] = useState<string | undefined>();
    const styledErrorRef = useRef<ElementRef<typeof StyledError>>(null);
    const [focused, setFocused] = useState(autoFocus || false);
    const inputElementRef = useRef<HTMLInputElement>(null);
    const placeholderRef = useRef<HTMLDivElement>(null);

    const [, forceReRender] = useReducer((x) => x + 1, 0);

    const id = useMemo(() => makeId(10), []);

    const isUsingPrefillValue = prefillValue && !_value;

    const onBlurInput = (val: string) => {
      onBlur?.(val);
      validate(val);
      setFocused(false);
    };

    const validate = (val: string | undefined) => {
      const _err = validator?.(val || '');
      if (!_err) {
        setError(undefined);
        return true;
      }
      setError(_err);
      styledErrorRef.current?.shakeError();
      return false;
    };

    const onFocusInput = (e: FocusEvent<HTMLInputElement>) => {
      e.target.select();
      setFocused(true);
    };

    useEffect(() => {
      setTimeout(() => {
        _setValue(value);
        forceReRender();
        if (value) {
          validate(value);
        }
      }, 0);
      // We are doing this to fix and issue with iris input
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useImperativeHandle(ref, () => ({
      validate: () => validate(_value),
      getValue: () => _value || '',
    }));

    useEffect(() => {
      autoFocus && inputElementRef.current?.focus();
    }, [autoFocus]);

    useEffect(() => {
      setError(errorMessage);
      styledErrorRef.current?.shakeError();
    }, [errorMessage]);

    const isEllipsis = useMemo(() => {
      if (placeholderRef.current) {
        return placeholderRef.current.offsetWidth < placeholderRef.current.scrollWidth;
      }
      return false;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placeholderRef.current]);

    const translateFieldName = (fieldName?: string) => {
      switch (fieldName) {
        case 'Email address':
          return translate('Email address');
        case 'First name':
          return translate('First name');
        case 'Last name':
          return translate('Last name');
        default:
          return fieldName;
      }
    };

    return (
      <Wrapper
        onClick={() => inputElementRef.current?.focus()}
        status={error ? 'negative' : 'neutral'}
        focused={focused}
      >
        <Tooltip tip={focused && isEllipsis ? translateFieldName(placeholder) : undefined}>
          <Placeholder
            ref={placeholderRef}
            className={
              isUsingPrefillValue || focused || (_value && _value.length > 0)
                ? 'animate-up'
                : 'animate-down'
            }
          >
            <label htmlFor={id}>{translateFieldName(placeholder)}</label>
            {required && <RedStar>*</RedStar>}
          </Placeholder>
        </Tooltip>
        <StyledInput
          id={id}
          ref={inputElementRef}
          onFocus={(e) => onFocusInput(e)}
          onBlur={(e) => {
            onBlurInput(e.target.value);
          }}
          onKeyDown={(e) => {
            e.key === KEY_CODES.ENTER && onEnterKeyPressed?.(_value || '');
            onClearPrefill?.();
          }}
          maxLength={REGISTRATION_FORM_MAX_CHARACTERS}
          onChange={(e) => {
            if (hasAutoFilled(e)) {
              onBlurInput(e.target.value);
            }
            _setValue(e.target.value);
          }}
          value={_value}
          aria-required={required}
        />
        {isUsingPrefillValue && (
          <ObsfucatedDefaultValue>
            <SelectedText selected={focused}>{prefillValue}</SelectedText>
          </ObsfucatedDefaultValue>
        )}
        {error && <StyledError error={error} ref={styledErrorRef} />}
      </Wrapper>
    );
  },
);

const Placeholder = styled.div`
  font-size: 14px;
  position: absolute;
  top: 6px;
  left: 9px;
  width: 95%;
  white-space: nowrap;
  text-align: left;
  text-overflow: ellipsis;
  overflow: hidden;
  color: ${({ theme }) => theme.additions.content.color2};
  &.animate-down {
    transform: translateY(10px);
    transition: transform 200ms;
  }
  &.animate-up {
    transform: translateY(0);
    transition: transform 200ms;
  }
`;

const RedStar = styled.span`
  color: red;
  padding-left: 2px;
`;

const Wrapper = styled.div<{ focused: boolean; status: 'negative' | 'neutral' }>`
  display: flex;
  position: relative;
  width: 100%;
  max-width: 360px;
  height: 51px;
  border-radius: 8px;
  background: white;
  ${({ focused }) =>
    focused
      ? css`
          border: 2px solid rgba(0, 173, 239, 1);
        `
      : css`
          border: 2px solid rgba(193, 193, 193, 1);
        `}
  ${({ status }) =>
    status === 'negative' &&
    css`
      border: 2px solid #e22b12;
    `}
  box-sizing: border-box;
`;

const StyledInput = styled.input`
  width: 100%;
  height: 24px;
  font-size: 14px;
  background: transparent;
  outline: none;
  border: none;
  padding-left: 8px;
  padding-right: 6px;
  padding-top: 22px;
  border-radius: 8px;
`;

const ObsfucatedDefaultValue = styled.div`
  position: absolute;
  top: 26px;
  left: 9px;
  font-size: 14px;
  width: 100%;
  white-space: nowrap;
  text-align: left;
  text-overflow: ellipsis;
  overflow: hidden;
  color: ${({ theme }) => theme.additions.content.color1};
  pointer-events: none;
`;

const SelectedText = styled.span<{ selected?: boolean }>`
  background-color: ${({ selected }) => (selected ? 'Highlight' : '')};
  color: ${({ selected }) => (selected ? 'HighlightText' : '')};
`;
